// Crystal data for Rock Remedies® - Complete crystal healing database
// Last updated: 2025-12-22
import amethystImg from '@/assets/crystals/amethyst.svg';
import roseQuartzImg from '@/assets/crystals/rose-quartz.svg';
import clearQuartzImg from '@/assets/crystals/clear-quartz.svg';
import citrineImg from '@/assets/crystals/citrine.svg';
import blackTourmalineImg from '@/assets/crystals/black-tourmaline.svg';
import lapisLazuliImg from '@/assets/crystals/lapis-lazuli.svg';
import greenAventurineImg from '@/assets/crystals/green-aventurine.svg';
import seleniteImg from '@/assets/crystals/selenite.svg';
import carnelianImg from '@/assets/crystals/carnelian.svg';
import labradoriteImg from '@/assets/crystals/labradorite.svg';
import sodaliteImg from '@/assets/crystals/sodalite.svg';
import moonstoneImg from '@/assets/crystals/moonstone.svg';
import lepidoliteImg from '@/assets/crystals/lepidolite.svg';
import howliteImg from '@/assets/crystals/howlite.svg';
import smokyQuartzImg from '@/assets/crystals/smoky-quartz.svg';
import tigersEyeImg from '@/assets/crystals/tigers-eye.svg';
import pyriteImg from '@/assets/crystals/pyrite.svg';
import rhodoniteImg from '@/assets/crystals/rhodonite.svg';
import amazoniteImg from '@/assets/crystals/amazonite.svg';
import fluoriteImg from '@/assets/crystals/fluorite.svg';
import obsidianImg from '@/assets/crystals/black-obsidian.svg';
import malachiteImg from '@/assets/crystals/malachite.svg';
import sunstoneImg from '@/assets/crystals/sunstone.svg';
import azuriteImg from '@/assets/crystals/azurite.svg';

export interface Crystal {
  id: string;
  name: string;
  image: string;
  description: string;
  properties: string[];
  chakras: string[];
  element: string;
  zodiacSigns: string[];
  careInstructions: string;
  cleansingMethods: string[];
  chargingMethods: string[];
  categories: string[];
  categoryReasons?: { [key: string]: string };
  categoryTaglines?: { [key: string]: string };
  scientificFacts: string;
  vibrationFrequency?: string;
}

export interface RemedyCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  crystals: string[];
  colour: string;
}

export const crystals: Crystal[] = [
  {
    id: 'amethyst',
    name: 'Amethyst',
    image: amethystImg,
    description: 'A powerful protective stone with a high spiritual vibration. Known as the "Stone of Sobriety" and "Stone of Spirituality", amethyst has been treasured for thousands of years for its calming and protective properties.',
    properties: [
      'Calms the mind and emotions',
      'Enhances intuition and spiritual awareness',
      'Promotes restful sleep and vivid dreams',
      'Helps with meditation and focus',
      'Supports emotional balance and stress relief'
    ],
    chakras: ['Third Eye', 'Crown'],
    element: 'Air',
    zodiacSigns: ['Pisces', 'Virgo', 'Aquarius', 'Capricorn'],
    careInstructions: 'Avoid prolonged sunlight as it may fade the colour. Keep away from heat sources.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing', 'Selenite plate'],
    chargingMethods: ['Full moon light', 'Meditation intention', 'Crystal cluster'],
    categories: ['anxiety', 'sleep', 'intuition', 'protection'],
    categoryReasons: {
      anxiety: 'Amethyst\'s calming vibration activates the third eye and crown chakras, quieting the overactive mind. Its natural lithium content (the same element used in mood-stabilising medications) helps ease anxious thoughts and promote tranquility.',
      sleep: 'Amethyst promotes deep, restful sleep by calming mental chatter and facilitating the transition from waking to sleeping consciousness. Its connection to the dream realm helps prevent nightmares and encourages peaceful, healing dreams.',
      intuition: 'As the premier third eye stone, amethyst opens the gateway to higher consciousness and psychic awareness. It enhances meditation, sharpens intuitive abilities, and helps you trust your inner knowing.',
      protection: 'Amethyst creates a protective bubble of light around the aura, transmuting negative energies and psychic attacks into love. Ancient Greeks believed it protected against intoxication and harmful influences.'
    },
    categoryTaglines: {
      anxiety: 'Calms overactive mind',
      sleep: 'Peaceful dreams',
      intuition: 'Third eye activation',
      protection: 'Transmutes negativity'
    },
    scientificFacts: 'Amethyst is a variety of quartz (SiO₂) coloured by iron impurities and irradiation. The piezoelectric properties of quartz crystals have made them essential in modern technology, from watches to computer chips. Quartz oscillators maintain precise frequencies due to the piezoelectric effect discovered by the Curie brothers in 1880.',
    vibrationFrequency: 'Amethyst resonates at a high spiritual frequency, measured at approximately 32,876 Hz - the same frequency associated with expanded consciousness and theta brain states.'
  },
  {
    id: 'rose-quartz',
    name: 'Rose Quartz',
    image: roseQuartzImg,
    description: 'The stone of unconditional love and infinite peace. Rose Quartz opens the heart chakra to all forms of love: self-love, family love, platonic love, and romantic love.',
    properties: [
      'Opens the heart to give and receive love',
      'Promotes self-love and acceptance',
      'Encourages forgiveness and compassion',
      'Attracts loving relationships',
      'Soothes emotional wounds and trauma'
    ],
    chakras: ['Heart'],
    element: 'Water',
    zodiacSigns: ['Taurus', 'Libra', 'Scorpio'],
    careInstructions: 'Keep away from direct sunlight to prevent fading. Safe with water for short periods.',
    cleansingMethods: ['Running water', 'Moonlight', 'Rose petals', 'Sound cleansing'],
    chargingMethods: ['Moonlight (especially full moon)', 'Buried in rose petals', 'Loving intention'],
    categories: ['love', 'anxiety', 'sleep'],
    categoryReasons: {
      love: 'Rose quartz is THE heart stone - its gentle pink vibration resonates directly with the heart chakra, dissolving emotional wounds and opening you to give and receive love unconditionally. It attracts soulmate connections and deepens existing bonds.',
      anxiety: 'The soothing, nurturing energy of rose quartz calms the nervous system and replaces anxiety with self-compassion. It reminds you that you are worthy of love and peace, easing the self-critical thoughts that fuel anxiety.',
      sleep: 'Rose quartz creates a cocoon of loving energy around you as you sleep, promoting feelings of safety and security. It\'s especially helpful for those who experience anxiety at bedtime or who need emotional healing during sleep.'
    },
    categoryTaglines: {
      love: 'Heart chakra stone',
      anxiety: 'Nurturing calm',
      sleep: 'Loving protection'
    },
    scientificFacts: 'Rose quartz gets its pink colour from trace amounts of titanium, iron, or manganese. Like all quartz, it has a hardness of 7 on the Mohs scale. The silicon dioxide structure creates a stable crystal lattice that has been used in technology for radio frequency control.',
    vibrationFrequency: 'Rose quartz vibrates at the frequency of unconditional love - approximately 350 Hz, which aligns with the heart chakra and promotes feelings of warmth and compassion.'
  },
  {
    id: 'clear-quartz',
    name: 'Clear Quartz',
    image: clearQuartzImg,
    description: 'Known as the "Master Healer", Clear Quartz is the most versatile healing stone. It amplifies energy and intention, enhances psychic abilities, and aids in meditation.',
    properties: [
      'Amplifies energy and intention',
      'Enhances clarity of thought',
      'Aids concentration and memory',
      'Harmonises all chakras',
      'Cleanses and charges other crystals'
    ],
    chakras: ['All chakras', 'Especially Crown'],
    element: 'All elements',
    zodiacSigns: ['All signs'],
    careInstructions: 'One of the most durable crystals. Safe with water and sunlight.',
    cleansingMethods: ['Running water', 'Sunlight', 'Moonlight', 'Salt water', 'Sound'],
    chargingMethods: ['Sunlight', 'Moonlight', 'Earth burial', 'Intention setting'],
    categories: ['clarity', 'abundance', 'depression', 'protection', 'intuition'],
    categoryReasons: {
      clarity: 'Clear quartz is pure crystallised light - it clears mental fog, enhances concentration, and brings crystal-clear thinking. As the "Master Healer", it amplifies clarity on all levels: mental, emotional, and spiritual.',
      abundance: 'Clear quartz amplifies whatever energy you programme into it, making it a powerful manifestation tool. Set your abundance intentions clearly and the crystal will amplify them into the universe.',
      depression: 'Clear quartz brings light into darkness, raising the overall vibration and clearing stagnant energy that contributes to low mood. It helps restore energy and vitality by harmonising all the chakras.',
      protection: 'Clear quartz creates a shield of pure white light around the aura, filtering out negative energies and strengthening your energetic boundaries. It amplifies the protective properties of other crystals.',
      intuition: 'Clear quartz enhances all psychic abilities by amplifying the energy flowing through the third eye and crown chakras. It acts as a spiritual antenna, receiving and transmitting higher guidance.'
    },
    categoryTaglines: {
      clarity: 'Master healer',
      abundance: 'Amplifies intentions',
      depression: 'Brings light',
      protection: 'White light shield',
      intuition: 'Spiritual antenna'
    },
    scientificFacts: 'Clear quartz (silicon dioxide) is piezoelectric - it generates an electric charge under mechanical stress. This property is why quartz crystals are used in watches, clocks, radios, and computers to maintain precise frequencies. A quartz watch oscillates at exactly 32,768 times per second.',
    vibrationFrequency: 'Clear quartz oscillates at a stable 32,768 Hz - the same frequency used in quartz watches and electronic precision instruments. This is why it\'s called the "Master Healer" - it brings all energy into alignment.'
  },
  {
    id: 'citrine',
    name: 'Citrine',
    image: citrineImg,
    description: 'The stone of manifestation, imagination, and personal will. Known as the "Merchant\'s Stone" for its ability to attract prosperity and success.',
    properties: [
      'Attracts abundance and prosperity',
      'Boosts confidence and personal power',
      'Promotes optimism and joy',
      'Encourages generosity and sharing',
      'Stimulates creativity and imagination'
    ],
    chakras: ['Solar Plexus', 'Sacral'],
    element: 'Fire',
    zodiacSigns: ['Aries', 'Gemini', 'Leo', 'Libra'],
    careInstructions: 'Avoid prolonged sunlight. Heat-treated citrine may fade more quickly than natural citrine.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing'],
    chargingMethods: ['Brief sunlight', 'Moonlight', 'Intention setting'],
    categories: ['abundance', 'confidence', 'depression'],
    categoryReasons: {
      abundance: 'Citrine is the ultimate manifestation stone - it carries the power of the sun, attracting wealth, success, and all good things. Merchants have placed it in cash registers for centuries to attract and maintain wealth.',
      confidence: 'Citrine activates the solar plexus chakra - your personal power centre. Its sunny energy dissolves self-doubt and ignites your inner fire, helping you step into your power and shine brightly.',
      depression: 'Like captured sunshine, citrine radiates warm, joyful energy that lifts the spirits and dispels darkness. It\'s one of the few crystals that doesn\'t hold negative energy, making it a constant source of positivity.'
    },
    categoryTaglines: {
      abundance: 'Merchant\'s stone',
      confidence: 'Inner fire',
      depression: 'Captured sunshine'
    },
    scientificFacts: 'Natural citrine gets its yellow colour from iron impurities in the quartz structure. Much commercial citrine is actually heat-treated amethyst or smoky quartz. The transformation occurs at temperatures around 470°C, demonstrating how crystal structures can be modified through controlled heating.',
    vibrationFrequency: 'Citrine vibrates at frequencies associated with joy and manifestation - approximately 532 Hz, resonating with the solar plexus and sacral chakras for personal power and creativity.'
  },
  {
    id: 'black-tourmaline',
    name: 'Black Tourmaline',
    image: blackTourmalineImg,
    description: 'One of the most powerful protective stones. Black Tourmaline creates a psychic shield that deflects negative energies, entities, and destructive forces.',
    properties: [
      'Powerful protection against negative energy',
      'Grounds and balances all chakras',
      'Transforms negative energy to positive',
      'Protects against electromagnetic radiation',
      'Promotes a sense of security and self-confidence'
    ],
    chakras: ['Root'],
    element: 'Earth',
    zodiacSigns: ['Capricorn', 'Scorpio'],
    careInstructions: 'Durable and hardy. Can be cleansed with water but avoid salt as it may damage the surface.',
    cleansingMethods: ['Running water', 'Earth burial', 'Moonlight', 'Sage/smudging'],
    chargingMethods: ['Sunlight', 'Earth burial', 'Intention setting'],
    categories: ['protection', 'anxiety', 'confidence'],
    categoryReasons: {
      protection: 'Black tourmaline is the ultimate protection stone - it creates an energetic force field that repels and blocks negative energies, psychic attacks, and environmental pollutants including EMF radiation from electronics.',
      anxiety: 'By grounding excess energy through the root chakra and creating a protective shield, black tourmaline helps anxious individuals feel safe and secure. It transforms fearful energy into calm, stable confidence.',
      confidence: 'Black tourmaline grounds you firmly in your power, creating a stable foundation from which to face challenges. When you feel protected and grounded, natural confidence emerges.'
    },
    categoryTaglines: {
      protection: 'Ultimate shield',
      anxiety: 'Grounding security',
      confidence: 'Stable foundation'
    },
    scientificFacts: 'Black tourmaline (schorl) is both piezoelectric AND pyroelectric - it generates electricity when heated or pressurized. This unique property was known to the Dutch who used heated tourmaline to draw ash from pipes (calling it "aschentrekker"). It can also produce negative ions, which some studies suggest may improve mood and air quality.',
    vibrationFrequency: 'Black tourmaline has a grounding frequency that neutralises and transforms negative energy. Its pyroelectric and piezoelectric properties create measurable electrical charge when heated or pressed.'
  },
  {
    id: 'lapis-lazuli',
    name: 'Lapis Lazuli',
    image: lapisLazuliImg,
    description: 'A stone of wisdom and truth. Revered since ancient Egypt, Lapis Lazuli encourages self-awareness, self-expression, and reveals inner truth.',
    properties: [
      'Stimulates wisdom and good judgement',
      'Enhances intellectual ability',
      'Encourages honesty and self-expression',
      'Deepens meditation and spiritual journeying',
      'Promotes clarity and objectivity'
    ],
    chakras: ['Third Eye', 'Throat'],
    element: 'Water',
    zodiacSigns: ['Sagittarius', 'Libra', 'Taurus'],
    careInstructions: 'Avoid water and harsh chemicals. Clean gently with a soft cloth.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing'],
    chargingMethods: ['Moonlight', 'Meditation intention', 'Selenite plate'],
    categories: ['clarity', 'intuition', 'love'],
    categoryReasons: {
      clarity: 'Lapis lazuli opens the third eye to reveal truth and enhance wisdom. Its deep blue energy clears confusion and brings the clarity needed to make wise decisions and see situations objectively.',
      intuition: 'Revered by ancient Egyptians as the stone of royalty and spiritual power, lapis lazuli activates psychic abilities and deepens meditation. Its connection to the third eye chakra makes it essential for developing inner vision.',
      love: 'Lapis lazuli promotes honest communication in relationships - the foundation of lasting love. By encouraging authenticity and truth, it helps create deeper, more genuine connections.'
    },
    categoryTaglines: {
      clarity: 'Wisdom & truth',
      intuition: 'Royal vision',
      love: 'Honest communication'
    },
    scientificFacts: 'Lapis Lazuli is a metamorphic rock composed primarily of lazurite, calcite, and pyrite. The vivid blue colour comes from the sulfur-containing mineral lazurite. For millennia, it was ground into ultramarine pigment - the most expensive pigment in Renaissance art, worth more than gold.',
    vibrationFrequency: 'Lapis lazuli resonates with the frequency of truth and wisdom - activating the third eye and throat chakras simultaneously for clear insight and authentic expression.'
  },
  {
    id: 'green-aventurine',
    name: 'Green Aventurine',
    image: greenAventurineImg,
    description: 'Known as the "Stone of Opportunity", Green Aventurine is considered the luckiest of all crystals, especially in manifesting prosperity and wealth.',
    properties: [
      'Attracts luck and opportunity',
      'Releases old patterns and promotes growth',
      'Calms anger and irritation',
      'Supports heart health and emotional balance',
      'Encourages perseverance and optimism'
    ],
    chakras: ['Heart'],
    element: 'Earth',
    zodiacSigns: ['Aries', 'Leo'],
    careInstructions: 'Safe with water. Avoid prolonged sunlight to prevent colour fading.',
    cleansingMethods: ['Running water', 'Moonlight', 'Sage/smudging', 'Earth burial'],
    chargingMethods: ['Sunlight (brief)', 'Moonlight', 'Near plants', 'Intention setting'],
    categories: ['abundance', 'love', 'anxiety'],
    categoryReasons: {
      abundance: 'Green aventurine is known as the "Gambler\'s Stone" for good reason - it attracts luck, opportunity, and prosperity. Its heart chakra connection ensures abundance flows not just materially but in all areas of life.',
      love: 'As a heart chakra stone, green aventurine opens the heart to new love while healing old wounds. It encourages compassion, empathy, and the willingness to take a chance on love again.',
      anxiety: 'Green aventurine has a soothing energy that calms nervous anxiety and racing thoughts. Its connection to nature and growth helps restore a sense of peace and optimism.'
    },
    categoryTaglines: {
      abundance: 'Lucky opportunity',
      love: 'Heart opening',
      anxiety: 'Peaceful optimism'
    },
    scientificFacts: 'Green aventurine is a form of quartz containing fuchsite (a chromium-rich mica) that gives it the characteristic sparkle called "aventurescence". This optical effect occurs when light reflects off the aligned plate-like inclusions within the stone.',
    vibrationFrequency: 'Green aventurine vibrates at heart chakra frequencies, promoting balance, growth, and the flow of abundance energy.'
  },
  {
    id: 'selenite',
    name: 'Selenite',
    image: seleniteImg,
    description: 'Named after the Greek goddess of the moon, Selene. Selenite is a powerful cleansing and charging crystal that brings clarity of mind and higher consciousness.',
    properties: [
      'Cleanses and charges other crystals',
      'Clears stagnant or negative energy',
      'Promotes mental clarity and insight',
      'Connects to higher realms and spirit guides',
      'Creates a peaceful, calm atmosphere'
    ],
    chakras: ['Crown', 'Third Eye'],
    element: 'Wind',
    zodiacSigns: ['Cancer', 'Taurus'],
    careInstructions: 'NEVER use water - selenite will dissolve. Keep dry and handle gently as it scratches easily.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing', 'Sunlight (brief)'],
    chargingMethods: ['Moonlight', 'Intention setting', 'Does not typically need charging as it is self-cleansing'],
    categories: ['clarity', 'sleep', 'intuition', 'protection'],
    categoryReasons: {
      clarity: 'Selenite is pure crystallised light - it clears mental fog instantly and brings brilliant clarity to any situation. It\'s the go-to crystal when you need to see clearly and think straight.',
      sleep: 'Selenite\'s high vibration clears negative energy from the sleep space and creates a peaceful, protective atmosphere. Place it by your bed to promote restful, undisturbed sleep and angelic dreams.',
      intuition: 'Selenite opens the crown chakra wide, creating a direct channel to higher guidance. It\'s the crystal of choice for connecting with angels, spirit guides, and accessing higher wisdom.',
      protection: 'Selenite doesn\'t just protect - it actively cleanses and purifies. It creates a high-vibration environment where lower energies cannot exist. Use it to clear and protect your home, office, or sacred space.'
    },
    categoryTaglines: {
      clarity: 'Crystallised light',
      sleep: 'Angelic dreams',
      intuition: 'Crown channel',
      protection: 'Cleanses & purifies'
    },
    scientificFacts: 'Selenite is a crystallised form of gypsum (calcium sulfate dihydrate). It has a hardness of only 2 on the Mohs scale, making it very soft. The famous "Cave of Crystals" in Mexico contains selenite crystals up to 12 metres long - the largest natural crystals ever found, formed over 500,000 years in mineral-rich, superheated water.',
    vibrationFrequency: 'Selenite vibrates at one of the highest frequencies of any crystal - it continuously cleanses and charges itself and anything nearby. This is why it\'s the only crystal that doesn\'t need cleansing.'
  },
  {
    id: 'carnelian',
    name: 'Carnelian',
    image: carnelianImg,
    description: 'A stabilising stone with high energy. Carnelian restores vitality and motivation, stimulates creativity, and gives courage.',
    properties: [
      'Boosts courage and confidence',
      'Stimulates creativity and passion',
      'Enhances vitality and energy',
      'Promotes positive life choices',
      'Overcomes negative conditioning and abuse'
    ],
    chakras: ['Sacral', 'Root'],
    element: 'Fire',
    zodiacSigns: ['Taurus', 'Cancer', 'Leo', 'Virgo'],
    careInstructions: 'Safe with water and sunlight. One of the more durable stones.',
    cleansingMethods: ['Running water', 'Sunlight', 'Moonlight', 'Sage/smudging'],
    chargingMethods: ['Sunlight', 'Earth burial', 'Intention setting'],
    categories: ['confidence', 'depression', 'love', 'abundance'],
    categoryReasons: {
      confidence: 'Carnelian is the stone of bold action and courage. It activates the sacral and root chakras, igniting your inner fire and giving you the confidence to pursue your goals fearlessly.',
      depression: 'Carnelian\'s warm, vibrant energy cuts through apathy and depression like a ray of sunshine. It restores motivation, vitality, and zest for life when you feel stuck or uninspired.',
      love: 'Carnelian awakens passion and sensuality, reigniting the spark in relationships. Its connection to the sacral chakra enhances intimacy and helps overcome sexual and emotional blocks.',
      abundance: 'Carnelian\'s energy of motivation and success helps you take the actions necessary to achieve your goals. Ancient Egyptians called it the "Stone of Sunset" and used it for protection and prosperity.'
    },
    categoryTaglines: {
      confidence: 'Bold courage',
      depression: 'Vibrant energy',
      love: 'Awakens passion',
      abundance: 'Motivated action'
    },
    scientificFacts: 'Carnelian is a variety of chalcedony (cryptocrystalline quartz) coloured by iron oxide impurities. Ancient Egyptians associated it with the sun god Ra and used it extensively in jewellery and amulets. The warmth of the stone when worn against the skin was believed to aid blood circulation.',
    vibrationFrequency: 'Carnelian vibrates with warm, energising frequencies that stimulate the sacral and root chakras - promoting vitality, creativity, and grounded confidence.'
  },
  {
    id: 'labradorite',
    name: 'Labradorite',
    image: labradoriteImg,
    description: 'A stone of magic and transformation, Labradorite is known for its remarkable play of colour called labradorescence. It protects the aura and enhances psychic abilities.',
    properties: [
      'Awakens psychic abilities and intuition',
      'Protects the aura from energy leaks',
      'Facilitates transformation and change',
      'Enhances strength and perseverance',
      'Reveals hidden truths and possibilities'
    ],
    chakras: ['Third Eye', 'Crown', 'Throat'],
    element: 'Water',
    zodiacSigns: ['Leo', 'Scorpio', 'Sagittarius'],
    careInstructions: 'Safe with water. Avoid harsh chemicals and prolonged heat.',
    cleansingMethods: ['Moonlight', 'Running water', 'Sage/smudging', 'Sound cleansing'],
    chargingMethods: ['Full moon light', 'Starlight', 'Northern Lights visualisation'],
    categories: ['intuition', 'protection', 'depression'],
    categoryReasons: {
      intuition: 'Labradorite is the premier stone for awakening psychic gifts. Its mystical flash represents the veil between worlds - work with it to develop clairvoyance, telepathy, and access to the Akashic records.',
      protection: 'Labradorite creates a force field around the aura, sealing energy leaks and preventing others from draining your vital force. It\'s essential for empaths, healers, and anyone working with the public.',
      depression: 'Labradorite illuminates inner magic and hidden potential, revealing that there\'s always more than meets the eye. It helps lift depression by showing you the unseen possibilities waiting to unfold.',
      confidence: 'Labradorite reveals your hidden strengths and inner magic, helping you see your true potential. Its transformative energy builds confidence through self-discovery and personal power.'
    },
    categoryTaglines: {
      intuition: 'Awakens psychic gifts',
      protection: 'Aura force field',
      depression: 'Reveals hidden magic',
      confidence: 'Inner strength'
    },
    scientificFacts: 'Labradorite is a feldspar mineral that displays a remarkable optical phenomenon called "labradorescence" - a rainbow-like iridescence caused by light diffracting within the stone\'s microscopic layers. First discovered in Labrador, Canada in 1770.',
    vibrationFrequency: 'Labradorite\'s unique optical properties create variable light frequencies, making it a powerful tool for accessing multiple dimensions and states of consciousness.'
  },
  {
    id: 'moonstone',
    name: 'Moonstone',
    image: moonstoneImg,
    description: 'Sacred to the moon and associated with feminine energy, Moonstone is the stone of new beginnings, intuition, and inner growth.',
    properties: [
      'Enhances intuition and psychic abilities',
      'Supports emotional balance and cycles',
      'Promotes new beginnings and fresh starts',
      'Connects to lunar energy and feminine wisdom',
      'Soothes emotional instability and stress'
    ],
    chakras: ['Crown', 'Third Eye', 'Sacral'],
    element: 'Water',
    zodiacSigns: ['Cancer', 'Libra', 'Scorpio'],
    careInstructions: 'Handle gently - moonstone can be fragile. Avoid harsh chemicals.',
    cleansingMethods: ['Moonlight (especially full and new moon)', 'Sound cleansing', 'Selenite'],
    chargingMethods: ['Full moon light', 'New moon setting intentions', 'Meditation'],
    categories: ['intuition', 'sleep', 'love', 'anxiety'],
    categoryReasons: {
      intuition: 'Moonstone is directly connected to lunar energy and the divine feminine. It enhances intuition, psychic abilities, and the ability to receive guidance from the unconscious through dreams and inner knowing.',
      sleep: 'Moonstone\'s gentle, nurturing energy promotes restful sleep and lucid dreaming. It helps regulate sleep cycles and is especially powerful for women who experience sleep disturbances connected to their menstrual cycle.',
      love: 'Moonstone opens the heart to nurturing love and helps balance the masculine and feminine within. It\'s traditionally given as a lover\'s gift and was believed to bring reconciliation to estranged lovers.',
      anxiety: 'Moonstone\'s calming, feminine energy soothes emotional instability and anxiety. It helps those who tend to overreact find emotional balance and teaches acceptance of life\'s natural cycles.'
    },
    categoryTaglines: {
      intuition: 'Lunar wisdom',
      sleep: 'Lucid dreaming',
      love: 'Nurturing balance',
      anxiety: 'Emotional harmony'
    },
    scientificFacts: 'Moonstone is a variety of feldspar that displays adularescence - a billowing, milky glow caused by light scattering between microscopic layers of two feldspar minerals (orthoclase and albite) as they slowly cool.',
    vibrationFrequency: 'Moonstone resonates with lunar frequencies, cycling with the moon phases and influencing the rhythms of the body and emotions. Its energy peaks at the full and new moon.'
  },
  {
    id: 'lepidolite',
    name: 'Lepidolite',
    image: lepidoliteImg,
    description: 'Known as the "Stone of Transition", Lepidolite contains natural lithium, making it one of the most effective crystals for emotional healing and anxiety relief.',
    properties: [
      'Reduces anxiety and depression naturally',
      'Supports emotional balance during transitions',
      'Promotes restful sleep and calms nightmares',
      'Encourages independence and self-love',
      'Dissipates negativity and obsessive thoughts'
    ],
    chakras: ['Third Eye', 'Heart', 'Crown'],
    element: 'Water',
    zodiacSigns: ['Libra', 'Pisces'],
    careInstructions: 'Avoid water as it can damage the mica layers. Cleanse gently.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing', 'Selenite'],
    chargingMethods: ['Moonlight', 'Amethyst cluster', 'Intention setting'],
    categories: ['anxiety', 'sleep', 'depression'],
    categoryReasons: {
      anxiety: 'Lepidolite contains natural lithium - the same element used in anxiety and mood-stabilising medications. This makes it nature\'s most powerful anti-anxiety stone, naturally calming the nervous system.',
      sleep: 'The natural lithium in lepidolite promotes deep, peaceful sleep and helps quiet racing thoughts at bedtime. It\'s especially effective for those who experience anxiety-related insomnia.',
      depression: 'Lepidolite\'s lithium content provides natural mood support, while its gentle vibration encourages emotional healing and self-love. It helps release old emotional patterns that contribute to depression.'
    },
    categoryTaglines: {
      anxiety: 'Natural lithium',
      sleep: 'Quiets racing thoughts',
      depression: 'Mood support'
    },
    scientificFacts: 'Lepidolite is a lilac-grey or rose-coloured lithium-bearing mica mineral. It contains up to 7% lithium, making it a commercial source of this element. Lithium is used in batteries and has well-documented mood-stabilising properties in psychiatry.',
    vibrationFrequency: 'Lepidolite\'s natural lithium content creates a stabilising, calming frequency that helps regulate the nervous system and emotional body.'
  },
  {
    id: 'howlite',
    name: 'Howlite',
    image: howliteImg,
    description: 'A calming stone that teaches patience and helps to eliminate rage and uncontrolled anger. Howlite calms communication and encourages emotional expression.',
    properties: [
      'Calms an overactive mind',
      'Reduces stress, anxiety, and anger',
      'Promotes peaceful sleep',
      'Aids in memory and learning',
      'Encourages patience and positive character traits'
    ],
    chakras: ['Crown', 'Third Eye'],
    element: 'Air',
    zodiacSigns: ['Gemini', 'Virgo'],
    careInstructions: 'Porous stone - avoid water and keep away from dyed materials.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing', 'Selenite'],
    chargingMethods: ['Moonlight', 'Selenite plate', 'Intention setting'],
    categories: ['sleep', 'anxiety', 'clarity'],
    categoryReasons: {
      sleep: 'Howlite is renowned as an insomnia remedy. Place it under your pillow to calm an overactive mind and promote peaceful, uninterrupted sleep. It\'s especially helpful for those who lie awake thinking.',
      anxiety: 'Howlite absorbs anger, stress, and anxiety like a sponge. Its calming energy helps slow racing thoughts and brings a sense of calm patience to stressful situations.',
      clarity: 'By calming the mind, howlite creates space for clear thinking. It helps you see past emotional reactions to understand the true nature of situations.'
    },
    categoryTaglines: {
      sleep: 'Insomnia remedy',
      anxiety: 'Absorbs stress',
      clarity: 'Calm mind'
    },
    scientificFacts: 'Howlite is a calcium borosilicate hydroxide mineral discovered in Nova Scotia in 1868. Its porous nature makes it readily accept dyes, which is why it\'s often dyed to simulate turquoise or other stones.',
    vibrationFrequency: 'Howlite vibrates at a slow, calming frequency that naturally slows brain waves and encourages the transition into restful states.'
  },
  {
    id: 'smoky-quartz',
    name: 'Smoky Quartz',
    image: smokyQuartzImg,
    description: 'A grounding and anchoring stone with a strong link to the earth and base chakras. Smoky Quartz is excellent for lifting depression and negativity.',
    properties: [
      'Powerful grounding and protection',
      'Absorbs and transmutes negative energy',
      'Relieves stress, fear, and anxiety',
      'Promotes positive, pragmatic thought',
      'Aids in detoxification on all levels'
    ],
    chakras: ['Root', 'Solar Plexus'],
    element: 'Earth',
    zodiacSigns: ['Scorpio', 'Sagittarius', 'Capricorn'],
    careInstructions: 'Safe with water and sunlight. Very durable stone.',
    cleansingMethods: ['Running water', 'Moonlight', 'Earth burial', 'Sunlight'],
    chargingMethods: ['Earth burial', 'Moonlight', 'Sunlight (brief)'],
    categories: ['anxiety', 'depression', 'protection'],
    categoryReasons: {
      anxiety: 'Smoky quartz grounds anxious energy down through the body and into the earth. It helps those who feel scattered or overwhelmed become centred and present in the moment.',
      depression: 'Smoky quartz gently absorbs and transmutes heavy, stagnant energy. It helps lift depression by clearing energetic blocks and encouraging positive, practical thinking.',
      protection: 'Smoky quartz is one of the most effective grounding stones for protection. It absorbs negative energy and EMF pollution, transmuting them into positive frequencies.'
    },
    categoryTaglines: {
      anxiety: 'Earth grounding',
      depression: 'Transmutes heaviness',
      protection: 'Absorbs negativity'
    },
    scientificFacts: 'Smoky quartz gets its colour from free silicon formed by natural irradiation of the silicon dioxide. The same process is used commercially to enhance colour in lighter specimens. It shares all the piezoelectric properties of clear quartz.',
    vibrationFrequency: 'Smoky quartz vibrates at grounding frequencies that anchor scattered energies and create a stable foundation for healing and transformation.'
  },
  {
    id: 'tigers-eye',
    name: 'Tiger\'s Eye',
    image: tigersEyeImg,
    description: 'A stone of protection that was traditionally carried as a talisman against ill wishes. Tiger\'s Eye brings good luck and enhances integrity and willpower.',
    properties: [
      'Boosts confidence and courage',
      'Enhances willpower and personal power',
      'Brings good luck and prosperity',
      'Sharpens the senses and attention',
      'Balances emotional extremes'
    ],
    chakras: ['Solar Plexus', 'Sacral', 'Root'],
    element: 'Fire, Earth',
    zodiacSigns: ['Capricorn', 'Leo'],
    careInstructions: 'Safe with water and sunlight. Durable and hardy stone.',
    cleansingMethods: ['Sunlight', 'Moonlight', 'Running water', 'Sage'],
    chargingMethods: ['Sunlight', 'Earth burial', 'Intention setting'],
    categories: ['confidence', 'abundance', 'protection'],
    categoryReasons: {
      confidence: 'Tiger\'s eye harnesses the power of the sun and earth together, creating unwavering confidence and courage. It helps you see your own abilities clearly and take action without fear.',
      abundance: 'Tiger\'s eye has been used for centuries to attract wealth and protect it. Its energy of focused intention and confidence helps you manifest prosperity through wise action.',
      protection: 'Like the eye of a tiger, this stone reflects back negative intentions and protects against ill wishes. It was traditionally carried by warriors and travellers for protection.'
    },
    categoryTaglines: {
      confidence: 'Sun & earth power',
      abundance: 'Attracts wealth',
      protection: 'Reflects negativity'
    },
    scientificFacts: 'Tiger\'s eye is formed when quartz replaces crocidolite fibers (a type of asbestos). The parallel fibers create the distinctive chatoyancy or "cat\'s eye" effect. The golden-brown colour comes from iron oxide staining.',
    vibrationFrequency: 'Tiger\'s eye combines the high vibration of quartz with grounding earth energy, creating a balanced frequency that enhances focus and manifestation.'
  },
  {
    id: 'pyrite',
    name: 'Pyrite',
    image: pyriteImg,
    description: 'Known as "Fool\'s Gold", Pyrite is actually a powerful stone for manifestation, protection, and increasing willpower. It symbolises the sun and fire energy.',
    properties: [
      'Attracts wealth and abundance',
      'Boosts confidence and vitality',
      'Protects against negative energy',
      'Stimulates intellect and memory',
      'Encourages positive action and persistence'
    ],
    chakras: ['Solar Plexus', 'Sacral', 'Root'],
    element: 'Fire, Earth',
    zodiacSigns: ['Leo', 'Aries'],
    careInstructions: 'Avoid water - pyrite can rust. Keep dry and cleanse with dry methods only.',
    cleansingMethods: ['Sage/smudging', 'Sound cleansing', 'Sunlight (brief)', 'Selenite'],
    chargingMethods: ['Sunlight', 'Citrine cluster', 'Intention setting'],
    categories: ['abundance', 'confidence', 'protection'],
    categoryReasons: {
      abundance: 'Pyrite is literally the stone of gold - it attracts money, prosperity, and good fortune. Its cubic structure represents stability and manifestation of material wealth.',
      confidence: 'Pyrite ignites the fire of personal power in the solar plexus. It overcomes feelings of inadequacy and inspires confident action toward your goals.',
      protection: 'Pyrite creates a defensive shield against all forms of negative energy, including environmental pollution. Its reflective surface mirrors negativity back to its source.'
    },
    categoryTaglines: {
      abundance: 'Stone of gold',
      confidence: 'Personal power',
      protection: 'Defensive shield'
    },
    scientificFacts: 'Pyrite is iron sulfide (FeS₂) and gets its name from the Greek word for fire - striking pyrite against steel produces sparks. This property made it essential for early fire-starting. Pyrite crystals often form perfect cubes, demonstrating nature\'s geometric precision.',
    vibrationFrequency: 'Pyrite vibrates with the energy of the sun and fire - frequencies that stimulate vitality, willpower, and the manifestation of material abundance.'
  },
  {
    id: 'rhodonite',
    name: 'Rhodonite',
    image: rhodoniteImg,
    description: 'An emotional balancer that nurtures love and encourages the brotherhood of humanity. Rhodonite heals emotional shock and panic.',
    properties: [
      'Heals emotional wounds and scars',
      'Promotes forgiveness and compassion',
      'Balances emotions during stressful times',
      'Encourages unconditional love',
      'Builds confidence and self-worth'
    ],
    chakras: ['Heart', 'Root'],
    element: 'Earth',
    zodiacSigns: ['Taurus', 'Scorpio'],
    careInstructions: 'Safe with water. Durable stone suitable for jewellery.',
    cleansingMethods: ['Running water', 'Moonlight', 'Sage/smudging', 'Sound cleansing'],
    chargingMethods: ['Moonlight', 'Near rose quartz', 'Intention setting'],
    categories: ['love', 'anxiety', 'confidence'],
    categoryReasons: {
      love: 'Rhodonite is the "rescue stone" for love - it heals emotional wounds from past relationships and opens the heart to love again. Its pink and black colours represent the integration of light and shadow in love.',
      anxiety: 'Rhodonite is a powerful first-aid stone for emotional shock, panic attacks, and anxiety. It calms the emotions while grounding them through its connection to the root chakra.',
      confidence: 'By healing old emotional wounds and self-doubt, rhodonite restores confidence and self-worth. It helps you recognise your talents and use them for the highest good.'
    },
    categoryTaglines: {
      love: 'Rescue stone',
      anxiety: 'First aid for panic',
      confidence: 'Restores self-worth'
    },
    scientificFacts: 'Rhodonite is a manganese silicate mineral. The pink colour comes from manganese, while the black veining is manganese oxide. The name comes from the Greek word "rhodon" meaning rose.',
    vibrationFrequency: 'Rhodonite vibrates at heart-healing frequencies that integrate emotional wounds and restore balance between the heart and root chakras.'
  },
  {
    id: 'sodalite',
    name: 'Sodalite',
    image: sodaliteImg,
    description: 'A stone of insight and intuition, Sodalite encourages rational thought, objectivity, and truth. It enhances self-esteem and self-acceptance.',
    properties: [
      'Enhances intuition and insight',
      'Promotes rational thinking and objectivity',
      'Encourages truth and authenticity',
      'Calms panic attacks and anxiety',
      'Balances emotions and calms the mind'
    ],
    chakras: ['Third Eye', 'Throat'],
    element: 'Water, Air',
    zodiacSigns: ['Sagittarius', 'Pisces'],
    careInstructions: 'Safe with water. Avoid prolonged sunlight which may fade colour.',
    cleansingMethods: ['Moonlight', 'Running water', 'Sage/smudging', 'Sound cleansing'],
    chargingMethods: ['Moonlight', 'Lapis lazuli cluster', 'Intention setting'],
    categories: ['intuition', 'anxiety', 'clarity'],
    categoryReasons: {
      intuition: 'Sodalite bridges rational thought and intuitive insight. It helps you trust your inner knowing while maintaining objectivity - the perfect balance of logic and intuition.',
      anxiety: 'Sodalite calms panic attacks and brings order to chaos. Its deeply calming blue energy soothes the mind and helps you see situations clearly rather than through the lens of fear.',
      clarity: 'Sodalite encourages rational thought and helps you see the truth of situations. It cuts through confusion and emotional bias to reveal what\'s really going on.'
    },
    categoryTaglines: {
      intuition: 'Logic meets intuition',
      anxiety: 'Calms panic',
      clarity: 'Reveals truth'
    },
    scientificFacts: 'Sodalite is a chloric sodium aluminium silicate. Some specimens fluoresce orange under UV light due to sulfur content. It was not widely known until the large deposits in Ontario, Canada were discovered in 1891.',
    vibrationFrequency: 'Sodalite vibrates at frequencies that harmonise the logical mind with intuitive insight, creating clarity and balanced perception.'
  },
  {
    id: 'amazonite',
    name: 'Amazonite',
    image: amazoniteImg,
    description: 'Known as the "Hope Stone" and "Stone of Courage", Amazonite empowers one to search within and discover one\'s own truths and integrity.',
    properties: [
      'Soothes emotional trauma and worry',
      'Filters out stress and negative energy',
      'Promotes truthful communication',
      'Balances masculine and feminine energies',
      'Enhances intuition and creativity'
    ],
    chakras: ['Heart', 'Throat'],
    element: 'Water, Earth',
    zodiacSigns: ['Virgo', 'Aquarius'],
    careInstructions: 'Avoid water and sunlight which can damage the stone.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing', 'Selenite'],
    chargingMethods: ['Moonlight', 'Near plants', 'Intention setting'],
    categories: ['anxiety', 'clarity', 'love'],
    categoryReasons: {
      anxiety: 'Amazonite absorbs stress and worry like a sponge, leaving calm clarity in their place. It filters out negative energy and EMF pollution, creating a peaceful energetic environment.',
      clarity: 'Amazonite helps you see both sides of a problem and different points of view. It encourages you to speak your truth while understanding the perspectives of others.',
      love: 'Amazonite opens both the heart and throat chakras, enabling authentic communication in relationships. It helps you express your feelings clearly and understand your partner\'s needs.'
    },
    categoryTaglines: {
      anxiety: 'Absorbs worry',
      clarity: 'Multiple perspectives',
      love: 'Authentic expression'
    },
    scientificFacts: 'Amazonite is a variety of potassium feldspar. Its blue-green colour was once thought to come from copper, but is now attributed to trace amounts of lead and water in the crystal structure.',
    vibrationFrequency: 'Amazonite vibrates at calming frequencies that filter stress and promote truthful, heart-centred communication.'
  },
  {
    id: 'fluorite',
    name: 'Fluorite',
    image: fluoriteImg,
    description: 'Known as the "Genius Stone", Fluorite is highly protective and stabilising. It increases concentration and helps in decision-making.',
    properties: [
      'Enhances mental clarity and focus',
      'Protects against electromagnetic stress',
      'Promotes quick thinking and absorption of new information',
      'Stabilises and grounds chaotic energy',
      'Encourages positivity and confidence'
    ],
    chakras: ['Third Eye', 'Heart', 'Throat'],
    element: 'Air, Water',
    zodiacSigns: ['Capricorn', 'Pisces'],
    careInstructions: 'Fragile - handle with care. Avoid water and sunlight.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing', 'Selenite'],
    chargingMethods: ['Moonlight', 'Clear quartz cluster', 'Intention setting'],
    categories: ['clarity', 'intuition', 'anxiety'],
    categoryReasons: {
      clarity: 'Fluorite is the ultimate clarity stone - it clears mental fog, improves concentration, and helps organise information. Students and scholars have treasured it for centuries.',
      intuition: 'Fluorite\'s multiple colours access different chakras simultaneously, enhancing intuition while keeping it grounded in reality. It helps discern when intuition is genuine guidance.',
      anxiety: 'Fluorite absorbs and neutralises stress and negative energy. Its stabilising influence brings order to chaos and helps you feel grounded and organised.'
    },
    categoryTaglines: {
      clarity: 'Genius stone',
      intuition: 'Grounded insight',
      anxiety: 'Stabilises chaos'
    },
    scientificFacts: 'Fluorite is calcium fluoride. The phenomenon of fluorescence was named after fluorite, which often glows under UV light. Its perfect octahedral cleavage makes it a popular specimen for crystal collectors.',
    vibrationFrequency: 'Fluorite vibrates at frequencies that organise and structure energy, bringing clarity and order to chaotic mental and emotional states.'
  },
  {
    id: 'obsidian',
    name: 'Obsidian',
    image: obsidianImg,
    description: 'A strongly protective stone, Obsidian forms a shield against negativity. It is a stone of truth that reveals secrets, including your own hidden truths.',
    properties: [
      'Powerful psychic protection',
      'Reveals truth and shadows',
      'Grounds spiritual energy into the body',
      'Blocks psychic attack and absorbs negativity',
      'Releases old emotional trauma'
    ],
    chakras: ['Root'],
    element: 'Earth, Fire',
    zodiacSigns: ['Scorpio', 'Sagittarius'],
    careInstructions: 'Safe with water. Can have sharp edges - handle carefully.',
    cleansingMethods: ['Running water', 'Moonlight', 'Sage/smudging', 'Earth burial'],
    chargingMethods: ['Moonlight', 'Earth burial', 'Intention setting'],
    categories: ['protection', 'depression', 'clarity'],
    categoryReasons: {
      protection: 'Obsidian is volcanic glass formed from rapidly cooled lava - it carries the power of fire and earth combined. It creates an impenetrable shield against negativity and psychic attack.',
      depression: 'Obsidian brings hidden issues to the surface for healing. While this can be intense, it allows you to release deep-seated patterns that contribute to depression.',
      clarity: 'Obsidian reveals truth, including truths you may have hidden from yourself. It cuts through illusion and denial to show you what you need to see for healing.',
      intuition: 'Obsidian\'s volcanic origins connect you to deep earth wisdom and primal intuition. It helps access hidden knowledge and enhances psychic abilities through grounding.'
    },
    categoryTaglines: {
      protection: 'Volcanic shield',
      depression: 'Surfaces for healing',
      clarity: 'Cuts through illusion',
      intuition: 'Deep earth wisdom'
    },
    scientificFacts: 'Obsidian is volcanic glass formed when lava cools so rapidly that mineral crystals don\'t have time to form. Ancient peoples prized it for making razor-sharp tools and weapons - obsidian blades can be sharper than surgical steel.',
    vibrationFrequency: 'Obsidian vibrates at deep, grounding frequencies that anchor and protect while simultaneously revealing hidden truths.'
  },
  {
    id: 'malachite',
    name: 'Malachite',
    image: malachiteImg,
    description: 'A stone of transformation, Malachite assists in clearing and activating the chakras. It absorbs negative energies and pollutants from the body and atmosphere.',
    properties: [
      'Powerful transformation and positive change',
      'Absorbs negative energy and pollutants',
      'Encourages risk-taking and change',
      'Breaks unwanted patterns and ties',
      'Opens the heart to unconditional love'
    ],
    chakras: ['Heart', 'Solar Plexus'],
    element: 'Fire, Earth',
    zodiacSigns: ['Scorpio', 'Capricorn'],
    careInstructions: 'TOXIC if ingested. Avoid water. Use with caution and always wash hands after handling.',
    cleansingMethods: ['Sage/smudging', 'Sound cleansing', 'Selenite', 'Moonlight'],
    chargingMethods: ['Moonlight', 'Quartz cluster', 'Intention setting'],
    categories: ['love', 'abundance', 'protection'],
    categoryReasons: {
      love: 'Malachite opens and clears the heart chakra, breaking old patterns that block love. It encourages healthy relationships by helping you recognise and release toxic dynamics.',
      abundance: 'Malachite is a stone of transformation that helps you break free from limiting beliefs about wealth. It encourages the risk-taking and change necessary for financial growth.',
      protection: 'Malachite absorbs negative energies and pollutants like a sponge - both from your body and your environment. It must be cleansed frequently due to this powerful absorption.'
    },
    categoryTaglines: {
      love: 'Breaks old patterns',
      abundance: 'Transformative growth',
      protection: 'Absorbs pollutants'
    },
    scientificFacts: 'Malachite is a copper carbonate hydroxide mineral. Its distinctive banded patterns form as copper-rich waters deposit layers of the mineral over time. Ancient Egyptians used it for eye makeup, and the Ural Mountains were famous for malachite deposits.',
    vibrationFrequency: 'Malachite vibrates at transformative frequencies that break old patterns and absorb negativity, creating space for positive change.'
  },
  {
    id: 'sunstone',
    name: 'Sunstone',
    image: sunstoneImg,
    description: 'A joyful, light-inspiring stone, Sunstone instills good nature and an enjoyment of life. It clears and energises all the chakras and brings sun energy.',
    properties: [
      'Brings joy, optimism and enthusiasm',
      'Encourages independence and originality',
      'Increases self-worth and confidence',
      'Clears energy and chakras',
      'Attracts fame, prosperity and success'
    ],
    chakras: ['Sacral', 'Solar Plexus'],
    element: 'Fire',
    zodiacSigns: ['Leo', 'Libra'],
    careInstructions: 'Safe with water and sunlight. Durable stone.',
    cleansingMethods: ['Sunlight', 'Running water', 'Moonlight', 'Sage'],
    chargingMethods: ['Sunlight', 'Fire light', 'Intention setting'],
    categories: ['depression', 'confidence', 'abundance'],
    categoryReasons: {
      depression: 'Sunstone is literally crystallised sunshine - it brings light into darkness and lifts the spirits. It\'s an anti-depressant stone that restores joy and optimism.',
      confidence: 'Sunstone empowers those who feel disadvantaged or discriminated against. It helps you say "no" when needed and provides the self-confidence to be your authentic self.',
      abundance: 'Sunstone attracts prosperity and success through its connection to the sun. It encourages independence and originality - qualities that lead to abundance.'
    },
    categoryTaglines: {
      depression: 'Crystallised sunshine',
      confidence: 'Authentic empowerment',
      abundance: 'Sun prosperity'
    },
    scientificFacts: 'Sunstone is a variety of feldspar that contains reflective inclusions of goethite or hematite, creating a sparkly effect called "aventurescence" or "schiller." Ancient Vikings may have used sunstone for navigation.',
    vibrationFrequency: 'Sunstone vibrates at warm, energising frequencies aligned with the sun - bringing light, joy, and vitality to all it touches.'
  },
  {
    id: 'azurite',
    name: 'Azurite',
    image: azuriteImg,
    description: 'Called the "Stone of Heaven", Azurite enhances intuition and spiritual guidance. It stimulates the third eye for insights and clear understanding.',
    properties: [
      'Enhances psychic abilities and intuition',
      'Clears confusion and opens the mind',
      'Stimulates intellectual abilities',
      'Releases stress and worry',
      'Facilitates meditation and spiritual development'
    ],
    chakras: ['Third Eye', 'Crown'],
    element: 'Air',
    zodiacSigns: ['Sagittarius', 'Aquarius'],
    careInstructions: 'Fragile and sensitive to heat, light, and water. Handle with care.',
    cleansingMethods: ['Moonlight', 'Sage/smudging', 'Sound cleansing'],
    chargingMethods: ['Moonlight', 'Clear quartz cluster', 'Intention setting'],
    categories: ['intuition', 'clarity', 'anxiety'],
    categoryReasons: {
      intuition: 'Azurite is one of the most powerful third eye stones - it stimulates psychic abilities, enhances meditation, and opens channels to divine guidance. Ancient cultures revered it as the stone of prophecy.',
      clarity: 'Azurite clears confusion and stimulates the intellect. It helps you understand complex situations and see the patterns and connections that lead to insight.',
      anxiety: 'Azurite releases stress, worry, and old thought patterns that create anxiety. It helps you understand the root causes of your fears and transmute them into wisdom.'
    },
    categoryTaglines: {
      intuition: 'Stone of prophecy',
      clarity: 'Stimulates intellect',
      anxiety: 'Releases old patterns'
    },
    scientificFacts: 'Azurite is a soft copper carbonate mineral prized for its deep blue colour. It often occurs with malachite and was ground into pigment for blue paint in the Middle Ages. It slowly transforms into malachite over time through a process called pseudomorphism.',
    vibrationFrequency: 'Azurite vibrates at high spiritual frequencies that activate the third eye and crown chakras, enhancing intuition and connection to higher guidance.'
  }
];

export const remedyCategories: RemedyCategory[] = [
  {
    id: 'anxiety',
    name: 'Alleviating Anxiety',
    description: 'Crystals that calm the nervous system, reduce stress, and promote a sense of peace and tranquility.',
    icon: 'Heart',
    crystals: ['amethyst', 'lepidolite', 'black-tourmaline', 'rose-quartz', 'smoky-quartz', 'howlite', 'sodalite', 'amazonite', 'rhodonite', 'moonstone', 'fluorite', 'green-aventurine'],
    colour: 'bg-muted/40'
  },
  {
    id: 'depression',
    name: 'Diminishing Depression',
    description: 'Stones that uplift mood, restore energy, and bring light and optimism back into your life.',
    icon: 'Sun',
    crystals: ['citrine', 'sunstone', 'carnelian', 'clear-quartz', 'lepidolite', 'smoky-quartz', 'labradorite', 'obsidian'],
    colour: 'bg-muted/30'
  },
  {
    id: 'sleep',
    name: 'Settling Sleep',
    description: 'Crystals that promote restful sleep, prevent nightmares, and encourage peaceful dreams.',
    icon: 'Moon',
    crystals: ['amethyst', 'lepidolite', 'howlite', 'selenite', 'moonstone', 'rose-quartz'],
    colour: 'bg-accent/20'
  },
  {
    id: 'abundance',
    name: 'Attracting Abundance',
    description: 'Prosperity stones that attract wealth, success, and opportunities into your life.',
    icon: 'Coins',
    crystals: ['citrine', 'pyrite', 'green-aventurine', 'tigers-eye', 'clear-quartz', 'carnelian', 'sunstone', 'malachite'],
    colour: 'bg-muted/35'
  },
  {
    id: 'love',
    name: 'Lasting Love',
    description: 'Heart-opening crystals that attract love, heal relationship wounds, and deepen connections.',
    icon: 'Heart',
    crystals: ['rose-quartz', 'rhodonite', 'green-aventurine', 'malachite', 'amazonite', 'moonstone', 'carnelian', 'lapis-lazuli'],
    colour: 'bg-muted/45'
  },
  {
    id: 'protection',
    name: 'Powerful Protection',
    description: 'Shielding stones that protect against negative energy, psychic attack, and environmental stress.',
    icon: 'Shield',
    crystals: ['black-tourmaline', 'obsidian', 'smoky-quartz', 'labradorite', 'amethyst', 'pyrite', 'tigers-eye', 'clear-quartz', 'selenite', 'malachite'],
    colour: 'bg-accent/15'
  },
  {
    id: 'clarity',
    name: 'Crystal Clarity',
    description: 'Stones that enhance mental clarity, focus, decision-making, and clear thinking.',
    icon: 'Lightbulb',
    crystals: ['clear-quartz', 'fluorite', 'selenite', 'sodalite', 'lapis-lazuli', 'howlite', 'amazonite', 'obsidian'],
    colour: 'bg-muted/25'
  },
  {
    id: 'confidence',
    name: 'Courageous Confidence',
    description: 'Empowering crystals that boost self-esteem, courage, and personal power.',
    icon: 'Zap',
    crystals: ['citrine', 'carnelian', 'tigers-eye', 'pyrite', 'sunstone', 'black-tourmaline', 'rhodonite', 'labradorite'],
    colour: 'bg-accent/25'
  },
  {
    id: 'intuition',
    name: 'Igniting Intuition',
    description: 'Third eye activating crystals that enhance psychic abilities, inner knowing, and spiritual connection.',
    icon: 'Eye',
    crystals: ['labradorite', 'amethyst', 'moonstone', 'azurite', 'lapis-lazuli', 'sodalite', 'selenite', 'fluorite', 'clear-quartz', 'obsidian'],
    colour: 'bg-muted/50'
  }
];

export const crystalCareGuide = {
  cleansing: {
    title: 'Cleansing Your Crystals',
    description: 'Regular cleansing removes accumulated energy and restores your crystal\'s natural vibration.',
    methods: [
      {
        name: 'Moonlight',
        description: 'Place crystals under the full moon overnight. Safe for all crystals and especially powerful for feminine energy stones.',
        safeFor: 'All crystals'
      },
      {
        name: 'Running Water',
        description: 'Hold under cool running water for 1-2 minutes while visualising negative energy washing away.',
        safeFor: 'Hard stones only (avoid selenite, malachite, calcite, lepidolite, pyrite)'
      },
      {
        name: 'Sage/Smudging',
        description: 'Pass crystals through the smoke of burning sage, palo santo, or incense.',
        safeFor: 'All crystals'
      },
      {
        name: 'Sound Cleansing',
        description: 'Use singing bowls, bells, or chimes to cleanse with sound vibrations.',
        safeFor: 'All crystals'
      },
      {
        name: 'Selenite Plate',
        description: 'Place crystals on a selenite charging plate or near selenite for 4-6 hours.',
        safeFor: 'All crystals'
      }
    ]
  },
  charging: {
    title: 'Charging Your Crystals',
    description: 'Charging restores and amplifies your crystal\'s energy after cleansing.',
    methods: [
      {
        name: 'Sunlight',
        description: 'Brief morning sunlight (1-2 hours max) for masculine energy stones.',
        safeFor: 'Clear quartz, carnelian, black tourmaline, tiger\'s eye (avoid amethyst, rose quartz, fluorite - they fade)'
      },
      {
        name: 'Moonlight',
        description: 'Full moon charging is ideal for all crystals, especially feminine energy stones.',
        safeFor: 'All crystals'
      },
      {
        name: 'Earth Burial',
        description: 'Bury in the earth for 24 hours to reconnect with grounding earth energy.',
        safeFor: 'All hard, non-porous crystals'
      },
      {
        name: 'Intention Setting',
        description: 'Hold the crystal, meditate, and programme it with your specific intention.',
        safeFor: 'All crystals'
      }
    ]
  },
  intentionSetting: {
    title: 'Setting Intentions',
    description: 'Programming your crystal with a specific intention amplifies its natural properties.',
    steps: [
      'Cleanse your crystal thoroughly',
      'Find a quiet space and hold the crystal in your hands',
      'Take several deep breaths to centre yourself',
      'Clearly state your intention aloud or in your mind',
      'Visualise the outcome you desire as already achieved',
      'Thank the crystal for its assistance',
      'Keep the crystal close to you or in a meaningful place'
    ]
  }
};

export const healingVibrations = {
  title: 'Crystal Healing Vibrations',
  introduction: 'Every crystal vibrates at a unique frequency. When we work with crystals, their stable frequencies help to realign and harmonise our own energy fields - a principle known as entrainment. Just as a tuning fork causes other forks nearby to vibrate at the same frequency, crystals help bring our bodies into energetic balance.',
  sections: [
    {
      title: 'Understanding Crystal Frequencies',
      content: 'All matter vibrates at the atomic level. Crystals, with their perfect, repeating molecular structures, vibrate at extremely stable and precise frequencies. This is why quartz is used in watches and electronics - its oscillation is so reliable it can keep time. When you hold a crystal, its stable vibration interacts with the more chaotic vibrations of your body, gently encouraging balance and alignment.'
    },
    {
      title: 'The Science of Resonance',
      content: 'Resonance occurs when one vibrating object causes another to vibrate at the same frequency. Studies have shown that heart cells in a petri dish will synchronise their beating when placed near each other. Similarly, the stable vibration of a crystal can help "tune" the vibration of your cells, organs, and energy centres (chakras) to their optimal frequencies.'
    },
    {
      title: 'High vs. Low Frequency Crystals',
      content: 'Higher frequency crystals (like selenite, amethyst, and clear quartz) connect with spiritual realms and upper chakras. Lower frequency crystals (like black tourmaline, smoky quartz, and obsidian) provide grounding and protection. Most healing involves using both high and low frequency stones to create balance between heaven and earth.'
    },
    {
      title: 'Working with Crystal Vibrations',
      content: 'To receive the vibrational benefits of crystals: hold them during meditation, wear them as jewellery, place them on your body during healing sessions, or simply keep them in your environment. The closer the crystal is to your body, the more directly its vibration can influence your energy field.'
    }
  ]
};

export const moonCeremonies = {
  title: 'Full & New Moon Crystal Ceremonies',
  introduction: 'The moon has been honoured in spiritual practices across all cultures. Its cycles provide a powerful framework for intention setting, release, and renewal. Crystals charged under the moon absorb lunar energy, amplifying their natural properties.',
  newMoon: {
    title: 'New Moon Ceremony - Planting Seeds',
    description: 'The new moon represents new beginnings, fresh starts, and planting seeds of intention. This is the ideal time to set goals, start new projects, and call in what you desire.',
    crystals: ['moonstone', 'labradorite', 'clear-quartz', 'citrine'],
    crystalReasons: {
      moonstone: 'Directly connected to lunar energy, moonstone amplifies new moon intentions and supports new beginnings.',
      labradorite: 'Helps you see hidden possibilities and plant seeds for transformation.',
      'clear-quartz': 'The "Master Healer" amplifies your intentions and sends them into the universe.',
      citrine: 'Attracts abundance and helps manifest your new moon wishes into reality.'
    },
    steps: [
      'Create a sacred space - light candles, burn sage or incense',
      'Gather your crystals and cleanse them with sage smoke',
      'Sit quietly and write down your intentions for the lunar cycle',
      'Hold each crystal and speak your intentions aloud',
      'Place crystals in a window or outside to absorb new moon energy overnight',
      'Keep your intention list somewhere sacred and review at full moon'
    ],
    affirmation: 'Under this new moon, I plant seeds of intention. I am ready to receive new blessings and opportunities. I trust the universe to support my growth.'
  },
  fullMoon: {
    title: 'Full Moon Ceremony - Harvest & Release',
    description: 'The full moon is a time of culmination, gratitude, and release. It\'s when intentions planted at the new moon reach fruition. It\'s also the most powerful time to cleanse and charge your crystals.',
    crystals: ['selenite', 'amethyst', 'rose-quartz', 'smoky-quartz'],
    crystalReasons: {
      selenite: 'Named after the moon goddess Selene, selenite becomes supercharged under moonlight and cleanses other crystals.',
      amethyst: 'Enhances spiritual connection and helps release what no longer serves your highest good.',
      'rose-quartz': 'Opens the heart to self-love and gratitude, essential for full moon reflection.',
      'smoky-quartz': 'Grounds and transmutes heavy energy, perfect for full moon release work.'
    },
    steps: [
      'As evening falls, gather all your crystals for cleansing',
      'Place them outside or on a windowsill under the full moon',
      'Write down what you wish to release - fears, patterns, blocks',
      'Safely burn the release list (or bury it in the earth)',
      'Hold rose quartz and list what you\'re grateful for',
      'Meditate with amethyst, connecting to the full moon\'s illumination',
      'Leave crystals overnight to fully charge'
    ],
    affirmation: 'Under this full moon, I release all that no longer serves me. I am grateful for my blessings. I am whole, complete, and illuminated by divine light.'
  },
  lunarTips: [
    'Crystals can be charged under both full and new moons, but full moon energy is most powerful for cleansing and charging',
    'Even if it\'s cloudy, the lunar energy still reaches your crystals',
    'If you can\'t place crystals outside, a windowsill works perfectly',
    'Try to work with the same crystals each moon cycle to build a relationship',
    'Keep a moon journal to track your intentions and their manifestations'
  ]
};

export const scienceSection = {
  title: 'The Science Behind Crystals',
  introduction: 'While crystals have been used for millennia in spiritual and healing practices, modern science has revealed fascinating properties that explain why these natural formations have captured human imagination.',
  sections: [
    {
      title: 'The Piezoelectric Effect',
      content: 'In 1880, Pierre and Jacques Curie discovered that certain crystals, particularly quartz, generate an electric charge when subjected to mechanical stress. This piezoelectric effect is why quartz crystals are essential in modern technology - from the precise timing in your watch (oscillating at exactly 32,768 Hz) to computer chips, radio transmitters, and ultrasound equipment. Every smartphone contains piezoelectric components derived from crystal technology.'
    },
    {
      title: 'Quartz in Technology',
      content: 'Silicon dioxide (quartz) is the foundation of modern computing. Computer chips are made from purified silicon derived from quartz sand. The stable crystalline structure allows for the precise control of electrical signals that powers our digital world. When you use a computer or phone, you\'re harnessing crystal technology.'
    },
    {
      title: 'Crystal Oscillators',
      content: 'Quartz crystal oscillators maintain frequencies with incredible precision - accurate to within one second per year. This is why they\'re used in GPS satellites, telecommunications, and scientific instruments. The natural resonance frequency of quartz crystals creates a stable reference point for measuring time and signals.'
    },
    {
      title: 'Colour and Chemistry',
      content: 'The colours in crystals arise from their atomic structure and trace impurities. Amethyst\'s purple comes from iron and irradiation, citrine\'s yellow from iron, and rose quartz\'s pink from titanium, iron, or manganese. These aren\'t just aesthetic - they reflect real differences in molecular composition and energy absorption patterns.'
    },
    {
      title: 'Pyroelectric & Triboelectric Properties',
      content: 'Some crystals like tourmaline are pyroelectric (generate electricity when heated) and triboelectric (generate electricity when rubbed). The Dutch discovered tourmaline could attract ashes when heated, calling it "aschentrekker" (ash drawer). These electrical properties demonstrate that crystals are not passive objects but active generators of energy.'
    },
    {
      title: 'Crystal Structure & Information Storage',
      content: 'Scientists are exploring crystals for data storage due to their stable molecular structure. A single quartz crystal can potentially store 360 terabytes of data for billions of years. This suggests that crystals\' famous ability to "hold and store energy" has a basis in their remarkable structural properties.'
    }
  ]
};

export interface Chakra {
  id: string;
  name: string;
  sanskritName: string;
  location: string;
  colour: string;
  colourClass: string;
  element: string;
  governs: string[];
  physicalAssociations: string[];
  emotionalAssociations: string[];
  signsOfBalance: string[];
  signsOfImbalance: string[];
  crystals: string[];
  healingPractices: string[];
  affirmation: string;
}

export const chakras: Chakra[] = [
  {
    id: 'root',
    name: 'Root Chakra',
    sanskritName: 'Muladhara',
    location: 'Base of spine, tailbone area',
    colour: 'Red',
    colourClass: 'bg-red-500',
    element: 'Earth',
    governs: ['Survival', 'Security', 'Grounding', 'Physical identity', 'Stability'],
    physicalAssociations: ['Legs', 'Feet', 'Bones', 'Large intestine', 'Adrenal glands', 'Immune system'],
    emotionalAssociations: ['Safety', 'Security', 'Belonging', 'Trust', 'Fear'],
    signsOfBalance: [
      'Feeling grounded and secure',
      'Healthy relationship with money and career',
      'Strong physical vitality',
      'Sense of belonging and connection to family/community',
      'Ability to meet basic needs with ease'
    ],
    signsOfImbalance: [
      'Anxiety and fearfulness',
      'Financial worries or instability',
      'Feeling disconnected or ungrounded',
      'Lower back pain, leg issues',
      'Eating disorders or weight issues',
      'Hoarding or excessive materialism'
    ],
    crystals: ['black-tourmaline', 'smoky-quartz', 'carnelian', 'obsidian'],
    healingPractices: [
      'Walk barefoot on earth (grounding/earthing)',
      'Practice yoga poses like Mountain, Warrior, and Tree',
      'Spend time in nature',
      'Use grounding crystals during meditation',
      'Eat red foods: beets, tomatoes, strawberries',
      'Visualise a spinning red wheel at the base of your spine'
    ],
    affirmation: 'I am safe, grounded, and secure. I have everything I need.'
  },
  {
    id: 'sacral',
    name: 'Sacral Chakra',
    sanskritName: 'Svadhisthana',
    location: 'Lower abdomen, about 2 inches below the navel',
    colour: 'Orange',
    colourClass: 'bg-orange-500',
    element: 'Water',
    governs: ['Creativity', 'Sexuality', 'Emotions', 'Pleasure', 'Relationships'],
    physicalAssociations: ['Reproductive organs', 'Bladder', 'Kidneys', 'Lower back', 'Hips'],
    emotionalAssociations: ['Joy', 'Passion', 'Creativity', 'Sensuality', 'Guilt'],
    signsOfBalance: [
      'Healthy emotional expression',
      'Creative flow and inspiration',
      'Comfortable with sexuality and intimacy',
      'Ability to experience pleasure without guilt',
      'Flexible and adaptable to change'
    ],
    signsOfImbalance: [
      'Emotional instability or numbness',
      'Creative blocks',
      'Sexual dysfunction or unhealthy relationships',
      'Addiction to pleasure (food, sex, substances)',
      'Hip or lower back problems',
      'Fear of change'
    ],
    crystals: ['carnelian', 'citrine', 'sunstone', 'moonstone'],
    healingPractices: [
      'Hip-opening yoga poses',
      'Dance and creative movement',
      'Spend time near water (baths, swimming, ocean)',
      'Practice creative activities without judgement',
      'Eat orange foods: oranges, carrots, sweet potatoes',
      'Allow yourself to experience pleasure mindfully'
    ],
    affirmation: 'I embrace pleasure and abundance. My creativity flows freely.'
  },
  {
    id: 'solar-plexus',
    name: 'Solar Plexus Chakra',
    sanskritName: 'Manipura',
    location: 'Upper abdomen, stomach area',
    colour: 'Yellow',
    colourClass: 'bg-yellow-500',
    element: 'Fire',
    governs: ['Personal power', 'Self-esteem', 'Willpower', 'Confidence', 'Transformation'],
    physicalAssociations: ['Digestive system', 'Stomach', 'Liver', 'Pancreas', 'Metabolism'],
    emotionalAssociations: ['Confidence', 'Self-worth', 'Personal power', 'Shame'],
    signsOfBalance: [
      'Strong sense of self and personal identity',
      'Confidence in abilities and decisions',
      'Healthy self-esteem',
      'Ability to set and maintain boundaries',
      'Good digestion and metabolism'
    ],
    signsOfImbalance: [
      'Low self-esteem or excessive ego',
      'Difficulty making decisions',
      'Feeling powerless or controlled by others',
      'Digestive issues (IBS, ulcers)',
      'Procrastination or lack of motivation',
      'Need to control others'
    ],
    crystals: ['citrine', 'tigers-eye', 'pyrite', 'sunstone'],
    healingPractices: [
      'Core-strengthening exercises',
      'Spend time in sunlight',
      'Practice saying "no" and setting boundaries',
      'Engage in activities that build confidence',
      'Eat yellow foods: bananas, corn, lemons',
      'Practice power poses and confident body language'
    ],
    affirmation: 'I am confident, powerful, and capable. I honour my authentic self.'
  },
  {
    id: 'heart',
    name: 'Heart Chakra',
    sanskritName: 'Anahata',
    location: 'Centre of chest, heart area',
    colour: 'Green (and Pink)',
    colourClass: 'bg-green-500',
    element: 'Air',
    governs: ['Love', 'Compassion', 'Forgiveness', 'Connection', 'Healing'],
    physicalAssociations: ['Heart', 'Lungs', 'Chest', 'Arms', 'Hands', 'Thymus gland'],
    emotionalAssociations: ['Love', 'Compassion', 'Joy', 'Grief', 'Forgiveness'],
    signsOfBalance: [
      'Capacity for deep, loving relationships',
      'Compassion for self and others',
      'Ability to forgive and let go',
      'Open to giving and receiving love',
      'Strong immune system and heart health'
    ],
    signsOfImbalance: [
      'Difficulty with intimacy or relationships',
      'Holding grudges or inability to forgive',
      'Feeling unloved or unworthy of love',
      'Co-dependency or poor boundaries in relationships',
      'Heart or lung problems',
      'Isolation or fear of connection'
    ],
    crystals: ['rose-quartz', 'green-aventurine', 'rhodonite', 'malachite', 'amazonite'],
    healingPractices: [
      'Heart-opening yoga poses (Camel, Bridge, Cobra)',
      'Practice gratitude daily',
      'Forgiveness meditation',
      'Spend time with loved ones',
      'Eat green foods: leafy greens, broccoli, green apples',
      'Practice self-love and self-care rituals'
    ],
    affirmation: 'I am open to love. I forgive myself and others. My heart is healed and whole.'
  },
  {
    id: 'throat',
    name: 'Throat Chakra',
    sanskritName: 'Vishuddha',
    location: 'Throat, neck area',
    colour: 'Blue',
    colourClass: 'bg-blue-500',
    element: 'Ether/Sound',
    governs: ['Communication', 'Expression', 'Truth', 'Authenticity', 'Listening'],
    physicalAssociations: ['Throat', 'Neck', 'Thyroid', 'Ears', 'Mouth', 'Shoulders'],
    emotionalAssociations: ['Expression', 'Truth', 'Communication', 'Lies', 'Authenticity'],
    signsOfBalance: [
      'Clear, authentic communication',
      'Ability to express thoughts and feelings',
      'Good listening skills',
      'Living in alignment with your truth',
      'Healthy thyroid function'
    ],
    signsOfImbalance: [
      'Difficulty expressing yourself',
      'Fear of speaking up or public speaking',
      'Talking too much or not listening',
      'Lying or being dishonest (including to yourself)',
      'Throat problems, thyroid issues',
      'Neck and shoulder tension'
    ],
    crystals: ['lapis-lazuli', 'sodalite', 'amazonite', 'azurite'],
    healingPractices: [
      'Singing, chanting, or humming',
      'Journaling to express thoughts',
      'Speak your truth kindly but clearly',
      'Neck stretches and shoulder rolls',
      'Eat blue foods: blueberries, blackberries',
      'Practice active listening'
    ],
    affirmation: 'I speak my truth with clarity and compassion. My voice matters.'
  },
  {
    id: 'third-eye',
    name: 'Third Eye Chakra',
    sanskritName: 'Ajna',
    location: 'Forehead, between the eyebrows',
    colour: 'Indigo',
    colourClass: 'bg-indigo-500',
    element: 'Light',
    governs: ['Intuition', 'Wisdom', 'Imagination', 'Insight', 'Perception'],
    physicalAssociations: ['Eyes', 'Brain', 'Pituitary gland', 'Sinuses', 'Sleep patterns'],
    emotionalAssociations: ['Intuition', 'Imagination', 'Wisdom', 'Illusion', 'Clarity'],
    signsOfBalance: [
      'Strong intuition and inner knowing',
      'Clear thinking and insight',
      'Vivid imagination and creativity',
      'Good memory and concentration',
      'Trust in inner guidance'
    ],
    signsOfImbalance: [
      'Difficulty concentrating or remembering',
      'Lack of intuition or ignoring gut feelings',
      'Headaches or vision problems',
      'Nightmares or sleep issues',
      'Feeling disconnected from reality',
      'Rigid thinking or closed-mindedness'
    ],
    crystals: ['amethyst', 'labradorite', 'lapis-lazuli', 'azurite', 'sodalite', 'fluorite'],
    healingPractices: [
      'Meditation, especially with eyes closed',
      'Dream journaling',
      'Trust and follow your intuition',
      'Reduce screen time',
      'Eat purple foods: grapes, purple cabbage, eggplant',
      'Practice visualisation exercises'
    ],
    affirmation: 'I trust my intuition. I see clearly and perceive the truth.'
  },
  {
    id: 'crown',
    name: 'Crown Chakra',
    sanskritName: 'Sahasrara',
    location: 'Top of the head',
    colour: 'Violet/White',
    colourClass: 'bg-violet-500',
    element: 'Cosmic Energy/Thought',
    governs: ['Spirituality', 'Enlightenment', 'Unity', 'Divine connection', 'Higher consciousness'],
    physicalAssociations: ['Brain', 'Nervous system', 'Pineal gland', 'Skull'],
    emotionalAssociations: ['Bliss', 'Presence', 'Spirituality', 'Attachment', 'Unity'],
    signsOfBalance: [
      'Sense of connection to something greater',
      'Spiritual awareness and growth',
      'Inner peace and contentment',
      'Wisdom and understanding',
      'Living with purpose and meaning'
    ],
    signsOfImbalance: [
      'Spiritual disconnection or cynicism',
      'Feeling lost or without purpose',
      'Excessive attachment to material things',
      'Depression or apathy',
      'Learning difficulties',
      'Spiritual bypassing (avoiding reality through spirituality)'
    ],
    crystals: ['amethyst', 'clear-quartz', 'selenite', 'moonstone', 'labradorite'],
    healingPractices: [
      'Meditation and prayer',
      'Spend time in silence',
      'Connect with nature',
      'Practice mindfulness and presence',
      'Fasting or light eating',
      'Study spiritual teachings that resonate with you'
    ],
    affirmation: 'I am connected to the divine. I am one with all that is.'
  }
];

export const chakraHealingGuide = {
  title: 'Crystal Chakra Healing',
  introduction: 'The chakra system describes seven major energy centres in the body, running from the base of the spine to the crown of the head. When these centres are balanced and flowing, we experience physical health, emotional stability, and spiritual connection. Crystals can be powerful tools for balancing and healing the chakras.',
  howToUse: [
    {
      method: 'Placement Meditation',
      description: 'Lie down comfortably. Place the appropriate crystal on or near each chakra. Breathe deeply and visualise each chakra spinning and glowing with its colour. Spend 5-10 minutes in this state.'
    },
    {
      method: 'Wearing Crystals',
      description: 'Wear crystal jewellery at the level of the chakra you wish to balance. A necklace near the heart, a bracelet for lower chakras, or earrings for upper chakras.'
    },
    {
      method: 'Meditation Focus',
      description: 'Hold a crystal associated with the chakra you want to work on. Focus on that area of your body while breathing deeply. Visualise the colour and imagine blockages dissolving.'
    },
    {
      method: 'Crystal Grid',
      description: 'Create a grid around your body with crystals corresponding to each chakra. This creates a healing energy field that works on all centres simultaneously.'
    }
  ]
};

// Psychological Framework & Professional Guidance
export const psychologicalFramework = {
  title: 'The Psychology of Crystal Practice',
  introduction: 'From a psychological perspective, working with crystals engages several well-documented therapeutic mechanisms. Understanding these can deepen your practice and help you use these tools more effectively.',
  disclaimer: {
    title: 'Important Professional Disclaimer',
    content: 'Crystal therapy is a complementary practice and should not replace professional mental health treatment, medical care, or prescribed medications. If you are experiencing significant psychological distress, please consult a qualified mental health professional. Crystals are tools for self-reflection, mindfulness, and ritual-they work best as part of a holistic approach to wellbeing that includes professional support when needed.',
    keyPoints: [
      'Crystals are complementary tools, not medical treatments',
      'Always consult healthcare providers for health concerns',
      'Crystal work enhances but does not replace therapy',
      'If in crisis, contact a mental health professional immediately'
    ]
  },
  sections: [
    {
      title: 'The Power of Ritual & Intention',
      icon: 'Target',
      content: 'Research in psychology shows that rituals-structured, symbolic actions-reduce anxiety, increase focus, and provide a sense of control. When you cleanse a crystal under moonlight or set an intention while holding it, you are engaging in a mindfulness practice that activates your prefrontal cortex (the brain\'s planning and intention centre) and calms your amygdala (the fear centre).',
      researchNote: 'Studies by Hobson et al. (2018) found that rituals reduce anxiety by creating predictable, controllable actions in uncertain situations.'
    },
    {
      title: 'Expectancy Effects & Self-Fulfilling Prophecy',
      icon: 'Sparkles',
      content: 'The "placebo effect" is not a weakness-it is evidence of your mind\'s remarkable healing power. When you believe a crystal will help you feel calm, your brain releases neurochemicals that actually create calmness. This is not "fake"-it is your mind-body connection in action. By choosing to work with crystals, you are harnessing your natural capacity for self-regulation.',
      researchNote: 'Benedetti (2014) demonstrated that expectation alone can activate endorphin and dopamine systems, producing real physiological changes.'
    },
    {
      title: 'Anchoring & Association',
      icon: 'Shield',
      content: 'In cognitive-behavioural psychology, an "anchor" is a stimulus that triggers a particular state. By consistently using a specific crystal during calm, meditative states, you create a neural association. Over time, simply holding that crystal can trigger the relaxation response-this is classical conditioning working in your favour.',
      researchNote: 'Based on Pavlovian conditioning principles, repeated pairing of a neutral stimulus with a desired state creates automatic associations.'
    },
    {
      title: 'Mindfulness & Present-Moment Awareness',
      icon: 'Eye',
      content: 'Examining a crystal\'s colours, weight, texture, and temperature is a form of mindfulness meditation. This sensory focus pulls attention away from rumination (repetitive negative thinking) and grounds you in the present moment-a core technique in Mindfulness-Based Stress Reduction (MBSR) and Acceptance and Commitment Therapy (ACT).',
      researchNote: 'Kabat-Zinn\'s MBSR research shows that present-moment sensory focus reduces stress hormones and improves emotional regulation.'
    },
    {
      title: 'Symbolic Meaning & Personal Narrative',
      icon: 'BookOpen',
      content: 'Humans are meaning-making creatures. When you assign symbolic meaning to a crystal (e.g., "rose quartz represents self-love"), you create a personal metaphor that shapes your thoughts and behaviours. This is similar to how narrative therapy uses stories and symbols to reframe experiences and support healing.',
      researchNote: 'White & Epston\'s narrative therapy research demonstrates how personal symbols and metaphors facilitate psychological change.'
    },
    {
      title: 'Self-Care Ritual & Self-Worth',
      icon: 'Heart',
      content: 'The act of caring for crystals-cleansing, charging, storing them safely-is a mirror for self-care. When you invest time in your crystal practice, you are sending yourself the message: "I am worth this attention." This simple act reinforces self-worth and creates a positive self-care feedback loop.',
      researchNote: 'Self-care behaviours are linked to improved self-esteem and reduced depression in multiple longitudinal studies.'
    }
  ],
  groundingExercise: {
    title: '5-4-3-2-1 Crystal Grounding',
    description: 'A validated grounding technique adapted for crystal practice. Use this when feeling anxious or disconnected.',
    steps: [
      'Hold your chosen crystal in your hands',
      'Name 5 things you can SEE about the crystal (colours, patterns, light)',
      'Name 4 things you can FEEL (temperature, weight, texture, edges)',
      'Name 3 things you can HEAR in your environment',
      'Name 2 things you can SMELL',
      'Name 1 thing you appreciate about yourself right now',
      'Take three deep breaths while holding the crystal to your heart'
    ],
    note: 'This exercise combines the proven 5-4-3-2-1 grounding technique with crystal mindfulness for enhanced effectiveness.'
  }
};

export const crystalSafetyGuide = {
  title: 'Crystal Safety & Care',
  introduction: 'While crystals are generally safe to handle and display, some require special care. This guide ensures you work with your crystals safely and responsibly.',
  generalSafety: [
    'Always wash hands after handling raw crystals, especially before eating',
    'Keep crystals away from young children and pets who might swallow small pieces',
    'Store crystals separately or wrapped to prevent scratching',
    'Never ingest crystals or crystal-infused water unless specifically prepared with water-safe stones',
    'Some crystals fade in sunlight-research each stone\'s care needs'
  ],
  toxicCrystals: [
    {
      name: 'Malachite',
      warning: 'Contains copper. Never use in water elixirs. Wash hands after handling raw/unpolished specimens. Safe when polished and dry.',
      safeUse: 'Display, meditation, holding briefly'
    },
    {
      name: 'Pyrite',
      warning: 'Can oxidise and release sulphur compounds when wet. Never use in water. Keep dry.',
      safeUse: 'Display, brief holding for intention setting'
    },
    {
      name: 'Selenite',
      warning: 'Water soluble-will dissolve and become rough. Never cleanse with water.',
      safeUse: 'Dry cleansing only (moonlight, sound, smoke)'
    },
    {
      name: 'Fluorite',
      warning: 'Contains fluorine. Not recommended for water elixirs. Handle gently as it\'s soft and can chip.',
      safeUse: 'Display, meditation, brief holding'
    },
    {
      name: 'Azurite',
      warning: 'Contains copper. Avoid water and prolonged skin contact with raw specimens.',
      safeUse: 'Display, brief meditation holding'
    },
    {
      name: 'Obsidian',
      warning: 'Volcanic glass with very sharp edges when broken. Handle carefully, especially raw specimens.',
      safeUse: 'Tumbled specimens are safe for all uses'
    }
  ],
  waterSafeCrystals: ['Clear Quartz', 'Rose Quartz', 'Amethyst', 'Citrine', 'Carnelian', 'Smoky Quartz', 'Aventurine'],
  sunlightSensitive: ['Amethyst', 'Rose Quartz', 'Fluorite', 'Celestite', 'Kunzite', 'Ametrine'],
  ethicalSourcing: {
    title: 'Ethical Crystal Sourcing',
    content: 'When purchasing crystals, consider their environmental and social impact. Look for sellers who can tell you where their crystals come from and avoid crystals from conflict zones or unsustainable mining practices.',
    tips: [
      'Ask sellers about the source and mining conditions',
      'Consider purchasing from small-scale artisan miners',
      'Reclaimed and vintage crystals are sustainable options',
      'Some crystals (like citrine) are often heat-treated amethyst-ask about treatment'
    ]
  }
};

export const journalingPrompts = {
  title: 'Crystal Reflection Journaling',
  introduction: 'Journaling enhances your crystal practice by creating a record of insights, tracking patterns, and deepening self-awareness. These prompts combine therapeutic reflection with crystal work.',
  categories: [
    {
      name: 'Intention Setting',
      prompts: [
        'What drew you to this particular crystal today? What does that attraction tell you about your current needs?',
        'If this crystal could speak, what message would it have for you right now?',
        'What intention will you set with this crystal? Why does this matter to you?',
        'How will you know when your intention has manifested? What will be different?'
      ]
    },
    {
      name: 'Emotional Exploration',
      prompts: [
        'As you hold this crystal, what emotions surface? Where do you feel them in your body?',
        'What past experience does this crystal\'s energy remind you of?',
        'If you could give your current emotional state a colour, what would it be? How does this relate to your crystal choice?',
        'What would self-compassion look like for you today? How might this crystal support that?'
      ]
    },
    {
      name: 'Pattern Recognition',
      prompts: [
        'Looking at your recent crystal choices, what patterns do you notice? What might these reveal?',
        'When do you tend to reach for grounding vs. energising crystals? What triggers each?',
        'How has your relationship with crystals evolved? What does this say about your personal growth?',
        'Which crystal challenge you the most? What resistance might you be working through?'
      ]
    },
    {
      name: 'Moon Ceremony Reflection',
      prompts: [
        'What are you ready to release with this moon cycle? How can you honour what you\'re letting go of?',
        'What new beginning is calling to you? What small step could you take toward it?',
        'How has the moon cycle affected your energy this month? What have you learned about your rhythms?',
        'What gratitude do you want to express to yourself for your journey so far?'
      ]
    },
    {
      name: 'Integration & Growth',
      prompts: [
        'What insight from your crystal practice do you want to carry into daily life?',
        'How might you share what you\'ve learned without preaching or imposing?',
        'What would it mean to trust yourself as much as you trust your crystals?',
        'If you no longer needed crystals as tools, what would that mean for your growth?'
      ]
    }
  ],
  trackingTemplate: {
    title: 'Session Tracking Template',
    fields: [
      'Date & Time',
      'Moon Phase',
      'Crystal(s) Used',
      'Intention/Purpose',
      'Physical Sensations',
      'Emotions Before',
      'Emotions After',
      'Insights Received',
      'Action Steps',
      'Gratitude Note'
    ]
  }
};

// Additional Therapeutic Exercises
export const crystalExercises = {
  title: 'Crystal Healing Exercises',
  introduction: 'These exercises combine evidence-based therapeutic techniques with crystal practice for enhanced mindfulness and self-regulation.',
  exercises: [
    {
      id: 'body-scan',
      name: 'Crystal Body Scan Meditation',
      duration: '10-15 minutes',
      difficulty: 'Beginner',
      description: 'A progressive relaxation technique using crystal placement to enhance body awareness and release tension.',
      benefits: ['Reduces physical tension', 'Improves body awareness', 'Promotes deep relaxation', 'Enhances mind-body connection'],
      crystals: ['Clear Quartz', 'Amethyst', 'Black Tourmaline'],
      steps: [
        'Choose a crystal that feels calming to you (amethyst or clear quartz work well)',
        'Lie down comfortably and place the crystal on your heart or hold it in your hands',
        'Close your eyes and take three deep breaths',
        'Starting at the top of your head, slowly move your attention down through your body',
        'At each area (head, face, neck, shoulders, arms, hands, chest, abdomen, hips, legs, feet), pause for 2-3 breaths',
        'Notice any tension without judgement-imagine the crystal\'s energy dissolving it',
        'When you reach your feet, take three more deep breaths',
        'Slowly return your awareness to the room'
      ],
      therapyNote: 'This exercise is based on the Body Scan meditation from Mindfulness-Based Stress Reduction (MBSR), developed by Jon Kabat-Zinn.'
    },
    {
      id: 'container',
      name: 'Crystal Container Exercise',
      duration: '5-10 minutes',
      difficulty: 'Beginner',
      description: 'A grounding technique for managing overwhelming emotions by symbolically containing them.',
      benefits: ['Emotional regulation', 'Reduces overwhelm', 'Creates psychological safety', 'Builds coping skills'],
      crystals: ['Obsidian', 'Smoky Quartz', 'Black Tourmaline', 'Hematite'],
      steps: [
        'When feeling overwhelmed, take a grounding crystal (obsidian or smoky quartz) in your hands',
        'Close your eyes and imagine a strong, beautiful container-a treasure chest, a safe, or a crystal geode',
        'Visualise the overwhelming feeling as a colour, shape, or substance',
        'Imagine placing this feeling into the container and closing the lid',
        'Know that it is safe there-you are not denying it, just containing it until you are ready to process it',
        'Hold your crystal and take three deep breaths',
        'When you feel ready, you can open the container with support (therapy, journaling, or when you feel resourced)',
        'Thank the crystal for holding space with you'
      ],
      therapyNote: 'The Container Exercise is a core technique in trauma-informed therapy and EMDR for managing emotional flooding.'
    },
    {
      id: 'safe-place',
      name: 'Crystal Safe Place Visualisation',
      duration: '10-15 minutes',
      difficulty: 'Beginner',
      description: 'Create a mental sanctuary enhanced by crystal energy for use during times of stress.',
      benefits: ['Reduces anxiety', 'Creates internal resource', 'Promotes feelings of safety', 'Accessible anytime'],
      crystals: ['Rose Quartz', 'Amethyst', 'Selenite', 'Blue Lace Agate'],
      steps: [
        'Hold a nurturing crystal like rose quartz or selenite',
        'Close your eyes and imagine a place where you feel completely safe and at peace',
        'This can be real or imaginary-a beach, forest, cosy room, or floating in clouds',
        'Engage all your senses: What do you see? Hear? Smell? Feel on your skin?',
        'Imagine your crystal glowing with warm light, amplifying the peace of this place',
        'Spend several minutes exploring this space, knowing nothing can harm you here',
        'Before leaving, create a "portal"-imagine you can return anytime by holding your crystal',
        'Slowly open your eyes, keeping the feeling of safety with you'
      ],
      therapyNote: 'Safe Place visualisation is used in EMDR and many trauma therapies to create an internal resource for self-regulation.'
    },
    {
      id: 'loving-kindness',
      name: 'Crystal Loving-Kindness Meditation',
      duration: '15-20 minutes',
      difficulty: 'Intermediate',
      description: 'A compassion meditation enhanced by heart-chakra crystals to cultivate self-love and connection.',
      benefits: ['Increases self-compassion', 'Reduces self-criticism', 'Improves relationships', 'Cultivates empathy'],
      crystals: ['Rose Quartz', 'Rhodonite', 'Green Aventurine', 'Amazonite'],
      steps: [
        'Hold a heart-chakra crystal (rose quartz is ideal) over your heart',
        'Close your eyes and take several deep breaths',
        'Begin by directing loving-kindness to yourself: "May I be happy. May I be healthy. May I be safe. May I live with ease."',
        'Feel the crystal warming with each phrase',
        'Next, bring to mind someone you love easily-repeat the phrases for them',
        'Then someone neutral (a stranger, shopkeeper)-repeat the phrases',
        'If you feel ready, bring to mind someone difficult-repeat the phrases',
        'Finally, extend to all beings: "May all beings be happy. May all beings be healthy..."',
        'Rest in the feeling of universal compassion'
      ],
      therapyNote: 'Loving-Kindness (Metta) meditation has extensive research support for reducing depression, anxiety, and increasing positive emotions.'
    },
    {
      id: 'parts-work',
      name: 'Crystal Parts Dialogue',
      duration: '20-30 minutes',
      difficulty: 'Advanced',
      description: 'Use different crystals to represent and dialogue with different parts of yourself.',
      benefits: ['Increases self-understanding', 'Resolves inner conflict', 'Integrates shadow aspects', 'Promotes self-acceptance'],
      crystals: ['Various-choose intuitively for each part'],
      steps: [
        'Identify an inner conflict (e.g., part that wants change vs. part that wants safety)',
        'Choose a crystal to represent each part-let intuition guide you',
        'Place the crystals in front of you with some space between them',
        'Hold the first crystal and speak AS that part: "I am the part that... I want... I fear..."',
        'Place it down and pick up the second crystal. Speak as that part',
        'Continue the dialogue, letting each part express fully without judgement',
        'When both feel heard, bring the crystals together in your hands',
        'Ask: "What do both parts ultimately want for me? What would integration look like?"',
        'Thank both parts for their wisdom and protectiveness'
      ],
      therapyNote: 'This exercise is inspired by Internal Family Systems (IFS) therapy and Gestalt "empty chair" technique.'
    },
    {
      id: 'anxiety-anchor',
      name: 'Anxiety Anchor Stone',
      duration: '2-5 minutes (plus conditioning)',
      difficulty: 'Beginner',
      description: 'Create a conditioned response where touching a specific crystal triggers calm.',
      benefits: ['Rapid anxiety relief', 'Portable coping tool', 'Builds over time', 'Discreet to use'],
      crystals: ['Lepidolite', 'Howlite', 'Amethyst', 'Blue Lace Agate'],
      steps: [
        'Choose a small, smooth crystal you can carry in your pocket',
        'During times when you feel naturally calm and relaxed, hold the crystal',
        'Focus on the sensations of calm: relaxed muscles, slow breathing, peaceful mind',
        'Repeat a calming word or phrase while holding the stone (e.g., "I am safe")',
        'Do this daily for 2-3 weeks to build the association',
        'When you feel anxious, hold the crystal, say your phrase, and recall the calm feelings',
        'The more you condition this response, the stronger it becomes',
        'Eventually, just touching the crystal can trigger the relaxation response'
      ],
      therapyNote: 'This technique uses classical conditioning (Pavlovian association) and is similar to "anchor" techniques used in NLP and guided relaxation.'
    },
    {
      id: 'gratitude-grid',
      name: 'Gratitude Crystal Grid',
      duration: '15-20 minutes',
      difficulty: 'Intermediate',
      description: 'Create a sacred geometry pattern with crystals while cultivating gratitude.',
      benefits: ['Shifts focus to positive', 'Reduces negativity bias', 'Creates lasting visual reminder', 'Combines creativity with mindfulness'],
      crystals: ['Citrine', 'Clear Quartz', 'Rose Quartz', 'Green Aventurine', 'Carnelian'],
      steps: [
        'Gather 7-12 crystals and find a quiet space',
        'Place a central crystal (citrine for abundance or clear quartz for amplification)',
        'As you place each surrounding crystal, speak aloud one thing you are grateful for',
        'Continue building outward, adding gratitude with each stone',
        'When complete, sit with your grid and feel the accumulated gratitude',
        'Take a photo if you wish to remember it',
        'Leave the grid in place as a visual reminder, or dismantle mindfully',
        'Each time you see or rebuild it, reconnect with gratitude'
      ],
      therapyNote: 'Gratitude practices have robust research support for improving wellbeing, with studies showing benefits lasting months after regular practice.'
    },
    {
      id: 'emotional-release',
      name: 'Crystal Emotional Release Ritual',
      duration: '20-30 minutes',
      difficulty: 'Intermediate',
      description: 'A structured ritual for safely processing and releasing stuck emotions.',
      benefits: ['Releases emotional blockages', 'Processes grief or anger', 'Creates closure', 'Provides catharsis'],
      crystals: ['Apache Tear', 'Obsidian', 'Smoky Quartz', 'Rhodonite'],
      steps: [
        'Choose a time when you have privacy and will not be interrupted',
        'Hold a crystal known for emotional release (apache tear, obsidian)',
        'Write on paper what you are releasing: a hurt, fear, old belief, or grief',
        'Read it aloud to your crystal, allowing any emotions to surface',
        'Cry, shake, breathe deeply-let your body release as needed',
        'When ready, burn or bury the paper (safely!) as a symbolic release',
        'Cleanse your crystal under running water or with smoke',
        'Hold a nurturing crystal (rose quartz) and speak words of comfort to yourself',
        'Rest, hydrate, and be gentle with yourself for the rest of the day'
      ],
      therapyNote: 'Ritual and symbolic action are powerful tools in grief therapy and trauma processing, providing concrete actions for abstract emotional processes.'
    }
  ]
};
